import { Component, Inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { FacilityItemInformation } from '../../../../common/models/facilityItemInformation';
import { LoggingService } from '../../../../services/logging.service';
import { ItemGroupEntity } from '../../../../common/models/itemGroupEntity';
import { ToastrService } from 'ngx-toastr';
import { FacilityManager } from '../../../../managers/facility.manager';
import { ItemsService } from '../../../../services/items.service';

@Component({
  selector: 'app-resolve-inconsistency',
  templateUrl: './resolve-inconsistency.component.html',
  styleUrls: ['./resolve-inconsistency.component.scss'],
  standalone: true,
  imports: [MatDialogContent, MatButton, MatFormField, MatLabel, MatSelect, MatOption, FormsModule],
})
export class ResolveInconsistencyComponent {
  public selectedMasterItem: FacilityItemInformation | null = null;
  public isClickable = true;
  public allItems: FacilityItemInformation[] = [];
  public inconsistentFields: string[] = [];

  private readonly handoffTypes: string[] = ['store', 'curbside', 'delivery', 'kiosk', 'tableside'];

  constructor(
    public dialogRef: MatDialogRef<ResolveInconsistencyComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ItemGroupEntity,
    private readonly itemsService: ItemsService,
    private readonly facilityManager: FacilityManager,
    private readonly toastr: ToastrService,
    private readonly logger: LoggingService,
  ) {
    this.allItems = this.data.itemGroups.flatMap((group) => group.items);
    this.inconsistentFields = this.detectInconsistentFields();
  }

  public getItemLabel(item: FacilityItemInformation): string {
    return item.itemName ? `${item.itemName} / ${item.itemDescription}` : item.itemDescription;
  }

  public getItemDetails(item: FacilityItemInformation): string[] {
    const details: string[] = [];

    if (this.inconsistentFields.includes('departmentName')) {
      details.push(`Department: ${item.departmentName}`);
    }

    if (this.inconsistentFields.includes('leadMinutes')) {
      details.push(`Lead Time: ${item.leadMinutes / 60} hr.`);
    }

    if (this.inconsistentFields.includes('prepMinutes')) {
      details.push(`Prep Time: ${item.prepMinutes} minutes`);
    }

    if (this.inconsistentFields.includes('postPrepMinutes')) {
      details.push(`Post Prep Time: ${item.postPrepMinutes ?? 0} minutes`);
    }

    if (this.inconsistentFields.includes('allowedHandoffTypes')) {
      const restrictions = this.handoffTypes
        .filter((handoffType) => !item.allowedHandoffTypes?.includes(handoffType))
        .map((handoffType) => handoffType.charAt(0).toUpperCase() + handoffType.slice(1));

      details.push(
        restrictions.length > 0
          ? `Hand-off Restrictions: Not available for ${restrictions.join(', ')}`
          : 'Hand-off Restrictions: None',
      );
    }

    if (this.inconsistentFields.includes('pickupDates')) {
      details.push(`Pickup Dates: ${this.formatDateRange(item.startPickupDate, item.endPickupDate)}`);
    }

    if (this.inconsistentFields.includes('soldQuantityDates')) {
      details.push(
        `Sold Quantity Dates: ${this.formatDateRange(item.startSoldQuantityDate, item.endSoldQuantityDate)}`,
      );
    }

    if (this.inconsistentFields.includes('isSellableQuantityEnabled')) {
      details.push(`Sellable Quantity Enabled: ${item.isSellableQuantityEnabled ? 'Yes' : 'No'}`);
    }

    if (this.inconsistentFields.includes('sellableQuantity')) {
      details.push(`Sellable Quantity: ${this.getComparableSellableQuantity(item)}`);
    }

    return details;
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onConfirm(): void {
    if (!this.selectedMasterItem || !this.isClickable) {
      return;
    }

    this.isClickable = false;
    const master = this.selectedMasterItem;
    const facilityId = this.facilityManager.getCurrentFacility();

    this.itemsService.syncGroup(facilityId, master.commerceItemNumber).subscribe({
      next: () => {
        const itemsToSync = this.allItems.filter((item) => item.commerceItemNumber !== master.commerceItemNumber);
        itemsToSync.forEach((item) => this.syncItemInMemory(master, item));
        this.toastr.success('Group successfully synced');
        this.dialogRef.close(itemsToSync);
      },
      error: (err) => {
        this.toastr.error('Error resolving inconsistency');
        this.logger.logException(err);
        this.isClickable = true;
      },
    });
  }

  private detectInconsistentFields(): string[] {
    if (this.allItems.length <= 1) {
      return [];
    }

    const baseline = this.allItems[0];
    const fields: string[] = [];

    if (this.allItems.some((item) => item.departmentName !== baseline.departmentName)) {
      fields.push('departmentName');
    }

    if (this.allItems.some((item) => item.leadMinutes !== baseline.leadMinutes)) {
      fields.push('leadMinutes');
    }

    if (this.allItems.some((item) => item.prepMinutes !== baseline.prepMinutes)) {
      fields.push('prepMinutes');
    }

    if (this.allItems.some((item) => (item.postPrepMinutes ?? 0) !== (baseline.postPrepMinutes ?? 0))) {
      fields.push('postPrepMinutes');
    }

    if (
      this.allItems.some(
        (item) =>
          this.normalizeHandoffTypes(item.allowedHandoffTypes)
          !== this.normalizeHandoffTypes(baseline.allowedHandoffTypes),
      )
    ) {
      fields.push('allowedHandoffTypes');
    }

    if (
      this.allItems.some(
        (item) =>
          this.normalizeDate(item.startPickupDate) !== this.normalizeDate(baseline.startPickupDate)
          || this.normalizeDate(item.endPickupDate) !== this.normalizeDate(baseline.endPickupDate),
      )
    ) {
      fields.push('pickupDates');
    }

    if (
      this.allItems.some(
        (item) =>
          this.normalizeDate(item.startSoldQuantityDate) !== this.normalizeDate(baseline.startSoldQuantityDate)
          || this.normalizeDate(item.endSoldQuantityDate) !== this.normalizeDate(baseline.endSoldQuantityDate),
      )
    ) {
      fields.push('soldQuantityDates');
    }

    if (this.allItems.some((item) => item.isSellableQuantityEnabled !== baseline.isSellableQuantityEnabled)) {
      fields.push('isSellableQuantityEnabled');
    }

    if (
      this.allItems.some(
        (item) =>
          item.isSellableQuantityEnabled
          && baseline.isSellableQuantityEnabled
          && this.getComparableSellableQuantity(item) !== this.getComparableSellableQuantity(baseline),
      )
    ) {
      fields.push('sellableQuantity');
    }

    return fields;
  }

  private getAdjustedSellableQuantity(master: FacilityItemInformation, target: FacilityItemInformation): number {
    const masterMultiplier = master.commodityGroupUnitQuantity ?? 1;
    const targetMultiplier = target.commodityGroupUnitQuantity ?? 1;

    return (master.sellableQuantity * masterMultiplier) / targetMultiplier;
  }

  private syncItemInMemory(master: FacilityItemInformation, target: FacilityItemInformation): void {
    target.departmentName = master.departmentName;
    target.leadMinutes = master.leadMinutes;
    target.prepMinutes = master.prepMinutes;
    target.postPrepMinutes = master.postPrepMinutes;
    target.allowedHandoffTypes = master.allowedHandoffTypes;
    target.startPickupDate = master.startPickupDate;
    target.endPickupDate = master.endPickupDate;
    target.isSellableQuantityEnabled = master.isSellableQuantityEnabled;
    target.sellableQuantity = master.isSellableQuantityEnabled ? this.getAdjustedSellableQuantity(master, target) : 0;
    target.startSoldQuantityDate = master.startSoldQuantityDate;
    target.endSoldQuantityDate = master.endSoldQuantityDate;
  }

  private normalizeDate(date?: Date | null): string {
    return date ? new Date(date).toISOString() : '';
  }

  private normalizeHandoffTypes(types?: string[] | null): string {
    return JSON.stringify((types ?? []).slice().sort((a, b) => a.localeCompare(b)));
  }

  private getComparableSellableQuantity(item: FacilityItemInformation): number {
    return (item.sellableQuantity ?? 0) * (item.commodityGroupUnitQuantity ?? 1);
  }

  private formatDateRange(start?: Date | null, end?: Date | null): string {
    if (!start && !end) {
      return 'Any';
    }

    const formattedStart = start ? new Date(start).toLocaleDateString() : 'N/A';
    const formattedEnd = end ? new Date(end).toLocaleDateString() : 'N/A';

    return `${formattedStart} - ${formattedEnd}`;
  }
}
